import { useMemo, useState } from "react";
import { Check, RotateCcw } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { CalculationDraft } from "@shared/calculation-template";

type CalculationTemplatePricingProps = {
  draft: CalculationDraft;
  onApply: (rate: number) => void;
  onReset: () => void;
  disabled?: boolean;
};

const euroRate = new Intl.NumberFormat("de-AT", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
});

function getRateState(draft: CalculationDraft) {
  const rates = draft.positions.map((position) => position.finalHourlyRate);
  const applied = rates.filter((rate): rate is number => rate !== undefined);
  const uniform = rates.length > 0 && applied.length === rates.length &&
    applied.every((rate) => rate === applied[0]);
  const mixed = applied.length > 0 && !uniform;
  return {
    rate: uniform ? applied[0] : undefined,
    mixed,
    hasOverride: applied.length > 0,
  };
}

function parseRate(value: string) {
  const normalized = value.trim().replace(",", ".");
  if (!normalized || !/^\d+(?:\.\d+)?$/.test(normalized)) return null;
  const rate = Number(normalized);
  return Number.isFinite(rate) && rate >= 0 && rate <= 1e9 ? rate : null;
}

export function CalculationTemplatePricing({ draft, onApply, onReset, disabled = false }: CalculationTemplatePricingProps) {
  const status = useMemo(() => getRateState(draft), [draft]);
  const [value, setValue] = useState(() => status.rate === undefined ? "" : String(status.rate));
  const [attempted, setAttempted] = useState(false);
  const parsedRate = parseRate(value);
  const isInvalid = parsedRate === null;

  const apply = () => {
    setAttempted(true);
    if (parsedRate === null) return;
    onApply(parsedRate);
    setAttempted(false);
  };

  const reset = () => {
    onReset();
    setValue("");
    setAttempted(false);
  };

  return (
    <section aria-labelledby="final-hourly-rate-title" className="rounded-xl border border-bauwerk-teal/30 bg-bauwerk-mint/30 p-4 shadow-sm sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id="final-hourly-rate-title" className="text-base font-semibold text-bauwerk-slate">Zentraler endgültiger Stundenverkaufssatz</h2>
            {status.mixed
              ? <span className="rounded-full border border-amber-500/35 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-900">Gemischte Sätze</span>
              : status.hasOverride
                ? <span className="rounded-full border border-bauwerk-teal/30 bg-background/70 px-2 py-0.5 text-[11px] font-medium text-bauwerk-dark-teal">Aktiv · {status.rate === undefined ? "gemischt" : euroRate.format(status.rate)} / Std</span>
                : <span className="rounded-full border border-border bg-background/70 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">K7-Sätze</span>}
          </div>
          <p className="mt-1 max-w-3xl text-xs leading-relaxed text-muted-foreground">
            Dieser Wert ist der endgültige Verkaufssatz je Arbeitsstunde — <strong className="font-semibold text-foreground">ohne zusätzlichen Lohnzuschlag</strong>.
            Materialpreise werden direkt je ME eingegeben; der Material-/Sonstiges-Zuschlag bleibt separat (K7-Sonstiges umfasst auch Geräte).
          </p>
        </div>
        {status.hasOverride && <Button type="button" size="sm" variant="outline" disabled={disabled} onClick={reset}>
          <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> K7-Sätze wiederherstellen
        </Button>}
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(180px,260px)_auto] sm:items-end">
        <div className="space-y-1.5">
          <Label htmlFor="final-hourly-rate">Endgültiger Verkaufssatz (€/Std)</Label>
          <div className="relative">
            <Input
              id="final-hourly-rate"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={value}
              disabled={disabled}
              aria-invalid={attempted && isInvalid}
              aria-describedby="final-hourly-rate-help"
              onChange={(event) => setValue(event.target.value)}
              placeholder="z. B. 58,50"
              className="h-10 pr-12 tabular-nums"
            />
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">€/Std</span>
          </div>
          <p id="final-hourly-rate-help" className="text-[11px] text-muted-foreground">
            {status.mixed
              ? "Vorlage enthält unterschiedliche aktive/ursprüngliche Sätze. Eingabe gilt nach Anwenden für alle Positionen."
              : status.rate !== undefined
                ? `Aktuell für alle Positionen: ${euroRate.format(status.rate)} je Stunde.`
                : "Wird erst mit „Auf alle Positionen anwenden“ aktiviert; bestehende K7-Werte bleiben bis dahin unverändert."}
          </p>
        </div>
        <Button type="button" disabled={disabled} onClick={apply} className="sm:mb-5">
          <Check className="mr-1.5 h-4 w-4" /> Auf alle Positionen anwenden
        </Button>
      </div>

      {attempted && isInvalid && <Alert variant="destructive" className="mt-3 py-2">
        <AlertDescription className="text-xs">Bitte einen gültigen, endlichen Satz zwischen 0 und 1.000.000.000 €/Std eingeben. Ein leeres Feld wird nicht als 0 übernommen.</AlertDescription>
      </Alert>}
      <p className="mt-3 border-t border-bauwerk-teal/15 pt-3 text-[11px] leading-relaxed text-muted-foreground">
        Änderungen gelten zunächst nur in diesem Entwurf. Speichere die Vorlage ausdrücklich; bestehende Angebote werden dadurch nicht automatisch aktualisiert.
      </p>
    </section>
  );
}
